/**
 * Maps the listPins RTK Query response to the ChatPinnedMessage shape consumed
 * by the header popover. Skips the request when no conversation is active.
 * Resolves pinned_by user_id to the Clerk org member's display name when
 * available, so the popover matches the avatars shown in-chat.
 */

import type { PinResponseDto } from '@saaga/shared/interfaces/message-pin.interface'
import { useListPinsQuery } from '@saaga/shared/store/message-pin-endpoints.store'
import { useMemo } from 'react'

import type { ChatPinnedMessage } from '@/components/chat/chat-header.interface'
import type { UserLookupMap } from '@/hooks/use-org-members-lookup.hook'

export const PINNED_MESSAGES_PAGE_SIZE = 200
export const PINNED_MESSAGES_INCLUDE_NOTES = true

interface UsePinnedMessagesParams {
  conversationId: string | null
  userLookup: UserLookupMap
}

export interface UsePinnedMessagesReturn {
  pinnedMessageList: ChatPinnedMessage[]
  isLoading: boolean
  isItemPinned: (itemId: string) => boolean
}

const resolvePinnedBy = (
  dto: PinResponseDto,
  userLookup: UserLookupMap
): ChatPinnedMessage['pinnedBy'] => {
  const match = userLookup.get(dto.pinned_by.user_id)
  return {
    id: dto.pinned_by.user_id,
    name: match?.name ?? dto.pinned_by.display_name ?? dto.pinned_by.user_id,
    avatarUrl: match?.avatarUrl ?? dto.pinned_by.avatar_url ?? undefined,
  }
}

const buildToChatPinnedMessage =
  (userLookup: UserLookupMap) =>
  (dto: PinResponseDto): ChatPinnedMessage => ({
    id: dto.item_id,
    parentMessageId: dto.parent_message_id,
    content: dto.item_preview ?? '',
    pinnedBy: resolvePinnedBy(dto, userLookup),
    pinnedAt: dto.pinned_at,
    pageNumber: dto.page_number,
    pageSize: dto.page_size,
    itemRole: dto.item_role ?? '',
  })

export const usePinnedMessages = ({
  conversationId,
  userLookup,
}: UsePinnedMessagesParams): UsePinnedMessagesReturn => {
  const { data, isLoading } = useListPinsQuery(
    {
      conversationId: conversationId ?? '',
      pageSize: PINNED_MESSAGES_PAGE_SIZE,
      includeNotes: PINNED_MESSAGES_INCLUDE_NOTES,
    },
    { skip: conversationId === null }
  )

  const pinnedMessageList = useMemo((): ChatPinnedMessage[] => {
    const mapper = buildToChatPinnedMessage(userLookup)
    return (data?.items ?? []).map(mapper)
  }, [data, userLookup])

  const pinnedIdSet = useMemo(
    (): Set<string> => new Set(pinnedMessageList.map((pin) => pin.id)),
    [pinnedMessageList]
  )

  const isItemPinned = (itemId: string): boolean => pinnedIdSet.has(itemId)

  return { pinnedMessageList, isLoading, isItemPinned }
}
